// The dashboard's state in the URL: `#/<view>?window=…&tenant=…&test=1`. A
// link opens the same view with the same filters, and Back returns to the
// view before: a view change adds a history entry, a filter change replaces
// the current one. The window is the view's own (VIEW_WINDOWS); a URL without
// tenant or test means all partners and no test sessions.

import { batch, effect } from "@preact/signals";

import type { WindowLabel } from "@/api/types";
import {
  currentView,
  includeTestSessions,
  isViewId,
  selectedTenant,
  VIEW_WINDOWS,
  type ViewId,
} from "@/state/filters";

export interface Route {
  view: ViewId;
  window?: WindowLabel;
  tenant: string;
  includeTest: boolean;
}

const ROUTE = /^#\/([^?]*)(?:\?(.*))?$/;

/** Whether `view` offers `value` as its time window. */
function isWindowOf(view: ViewId, value: string): value is WindowLabel {
  const options: readonly string[] = VIEW_WINDOWS[view]?.options ?? [];
  return options.includes(value);
}

/** The route a URL hash names; empty when it names no view of this dashboard.
 * A window the view does not offer is dropped. */
export function parseRoute(hash: string): Partial<Route> {
  const match = ROUTE.exec(hash);
  if (!match || !isViewId(match[1])) return {};
  const view = match[1];
  const params = new URLSearchParams(match[2] ?? "");
  const span = params.get("window") ?? "";
  return {
    view,
    ...(isWindowOf(view, span) ? { window: span } : {}),
    tenant: params.get("tenant") ?? "",
    includeTest: params.get("test") === "1",
  };
}

/** The URL hash for a route, carrying only the filters that are set. */
export function formatRoute(route: Route): string {
  const params = new URLSearchParams();
  if (route.window) params.set("window", route.window);
  if (route.tenant) params.set("tenant", route.tenant);
  if (route.includeTest) params.set("test", "1");
  const query = params.toString();
  return `#/${route.view}${query ? `?${query}` : ""}`;
}

/** The route the dashboard shows now. */
export function currentRoute(): Route {
  const view = currentView.value;
  return {
    view,
    window: VIEW_WINDOWS[view]?.signal.value,
    tenant: selectedTenant.value,
    includeTest: includeTestSessions.value,
  };
}

/** Shows a route; a window the view does not offer is left as it is. */
export function applyRoute(route: Partial<Route>): void {
  batch(() => {
    if (route.view) currentView.value = route.view;
    const view = currentView.peek();
    const windowed = VIEW_WINDOWS[view];
    if (windowed && route.window && isWindowOf(view, route.window)) {
      windowed.signal.value = route.window;
    }
    if (route.tenant !== undefined) selectedTenant.value = route.tenant;
    if (route.includeTest !== undefined) includeTestSessions.value = route.includeTest;
  });
}

/** Keeps the URL and the dashboard in step until the returned function runs. */
export function startRouting(win: Window = window): () => void {
  const fromUrl = () => applyRoute(parseRoute(win.location.hash));
  fromUrl();

  let shownView = currentView.peek();
  const stop = effect(() => {
    const hash = formatRoute(currentRoute());
    const view = currentView.value;
    if (hash !== win.location.hash) {
      if (view !== shownView) win.history.pushState(null, "", hash);
      else win.history.replaceState(null, "", hash);
    }
    shownView = view;
  });

  // Back / Forward, and a hash typed into the address bar.
  win.addEventListener("popstate", fromUrl);
  win.addEventListener("hashchange", fromUrl);
  return () => {
    stop();
    win.removeEventListener("popstate", fromUrl);
    win.removeEventListener("hashchange", fromUrl);
  };
}
